/**
 * Timeline
 *
 * A component for displaying interactive timelines in chat messages
 *
 * Primary responsibilities:
 * - Display events in chronological order
 * - Allow collapsing/expanding of events
 * - Support horizontal and vertical layouts based on screen size
 */

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  IconClock,
  IconChevronRight,
  IconChevronDown,
  IconArrowsShuffle
} from '@tabler/icons-react'
import { motion, AnimatePresence } from 'framer-motion'

interface TimelineEvent {
  date: string
  title: string
  description: string
}

interface TimelineProps {
  events: TimelineEvent[]
  className?: string
}

export const Timeline: React.FC<TimelineProps> = ({ events, className }) => {
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set())
  const [showAll, setShowAll] = useState(false)

  const toggleEvent = (index: number) => {
    const newExpandedEvents = new Set(expandedEvents)
    if (newExpandedEvents.has(index)) {
      newExpandedEvents.delete(index)
    } else {
      newExpandedEvents.add(index)
    }
    setExpandedEvents(newExpandedEvents)
  }

  const expandAll = () => {
    const allIndices = new Set(events.map((_, index) => index))
    setExpandedEvents(allIndices)
    setShowAll(true)
  }

  const collapseAll = () => {
    setExpandedEvents(new Set())
    setShowAll(false)
  }

  const toggleView = () => {
    if (showAll) {
      collapseAll()
    } else {
      expandAll()
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 25,
        duration: 0.4
      }}
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <motion.div
        className="flex items-center border-b p-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        <div className="flex items-center gap-2">
          <IconClock size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">Timeline</h3>
          <motion.span
            className="text-muted-foreground text-xs"
            animate={{ scale: events.length > 0 ? [1, 1.1, 1] : 1 }}
            transition={{ duration: 0.3 }}
          >
            ({events.length} events)
          </motion.span>
        </div>
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Button variant="ghost" size="sm" onClick={toggleView} className="ml-auto text-xs">
            {showAll ? 'Collapse All' : 'Expand All'}
          </Button>
        </motion.div>
      </motion.div>

      <div className="p-0">
        <div className="relative">
          {events.map((event, index) => (
            <motion.div
              key={index}
              className={cn(
                'relative ml-4 border-l-2 border-blue-400 pb-2 dark:border-blue-600',
                index === events.length - 1 ? 'border-l-0' : ''
              )}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                delay: 0.2 + index * 0.1,
                duration: 0.4,
                ease: 'easeOut'
              }}
              whileHover={{
                scale: 1.01,
                transition: { duration: 0.2 }
              }}
            >
              {/* Timeline dot */}
              <motion.div
                className="absolute -left-[5px] top-0 size-[10px] rounded-full bg-blue-400 dark:bg-blue-600"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  delay: 0.3 + index * 0.1,
                  type: 'spring',
                  stiffness: 400,
                  damping: 25
                }}
                whileHover={{
                  scale: 1.3,
                  transition: { duration: 0.2 }
                }}
              />

              <div className="pl-6 pt-1">
                <motion.div
                  className="flex cursor-pointer items-start gap-2"
                  onClick={() => toggleEvent(index)}
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="text-muted-foreground mt-1 whitespace-nowrap text-xs">
                    {event.date}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-1">
                      <motion.div
                        animate={{ rotate: expandedEvents.has(index) ? 0 : -90 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                      >
                        <IconChevronDown size={14} className="text-muted-foreground" />
                      </motion.div>
                      <h4 className="text-sm font-medium leading-none">{event.title}</h4>
                    </div>

                    <AnimatePresence>
                      {expandedEvents.has(index) && (
                        <motion.p
                          className="text-muted-foreground mt-2 text-sm"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                        >
                          {event.description}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  )
}
